// Per-field dirty tracking + durable flush queue for multi-device sync.
//
// Edits on the journal screen used to PUT the entire `today` blob on every
// 500ms debounce — two devices editing different fields would silently
// overwrite each other. This queue lives between the input handler and
// the network layer:
//
//   - markDirty(type, id, field, value) records a per-field change with
//     a client timestamp, persists to localStorage, and schedules a
//     debounced flush.
//   - flush() coalesces all pending entries for the record into a single
//     PATCH whose body is { patches, clientFieldTimestamps }. The server
//     does field-level LWW and returns the reconciled state.
//   - On visibilitychange/beforeunload we fire any pending flush
//     synchronously via fetch with `keepalive: true` — sendBeacon is
//     POST-only and we need PATCH.
//   - On network failure entries stay queued; an `online` event
//     listener retries when connectivity returns.
//
// The localStorage layer is what makes this resilient across mobile
// network drops, tab kills, and the daily Heroku dyno restart.

import { debounce } from "lodash";

const STORAGE_KEY = "nwd_sync_queue_v1";
const DEVICE_ID_KEY = "nwd_correlation_id";
const DEBOUNCE_MS = 500;

// recordKey -> Promise. Serializes flushes per record so a fresh PATCH
// never races an in-flight one for the same id.
const inflightByRecord = new Map();

// recordKey -> debounced flusher. One instance per record so debounces
// for different days don't bleed into each other.
const debouncedFlushers = new Map();

// Sender registry — DayScreen calls registerSender('day', fn) once.
// Decouples this module from Redux/axios.
const senders = new Map();

// Status emitter — screens subscribe via onStatusChange() to render a
// save-status pill ("Saving…" / "Saved" / "Couldn't save — retrying").
// Without this the queue was silent on failure: errors only hit
// console.warn and the user saw no acknowledgement that a save did or
// didn't land.
const statusListeners = new Set();

export function onStatusChange(listener) {
  statusListeners.add(listener);
  return () => statusListeners.delete(listener);
}

function emitStatus(event) {
  for (const fn of statusListeners) {
    try {
      fn(event);
    } catch (e) {
      // A misbehaving listener must never break the flush pipeline.
    }
  }
}

function readQueue() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (e) {
    return {};
  }
}

function writeQueue(queue) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
  } catch (e) {
    // Quota or private-mode failure — keep going. Worst case the in-memory
    // edits still flush during this session.
  }
}

function getRecordEntries(queue, type, id) {
  return (queue[type] && queue[type][id]) || null;
}

function recordKey(type, id) {
  return `${type}::${id}`;
}

export function getOrCreateDeviceId() {
  try {
    let id = localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      id =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `dev-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
  } catch (e) {
    return `dev-fallback-${Date.now()}`;
  }
}

export function registerSender(recordType, sender) {
  senders.set(recordType, sender);
}

// One-time cleanup: discard queue entries older than 24h. Prevents zombie
// entries from a past bug (e.g. permanently-rejected fields before the fix)
// from poisoning the queue for returning users.
const STALE_ENTRY_MS = 24 * 60 * 60 * 1000;

function purgeStaleEntries() {
  const queue = readQueue();
  const now = Date.now();
  let mutated = false;
  for (const [type, byId] of Object.entries(queue)) {
    for (const [recordId, entries] of Object.entries(byId)) {
      for (const [field, entry] of Object.entries(entries)) {
        if (entry.ts && now - entry.ts > STALE_ENTRY_MS) {
          delete entries[field];
          mutated = true;
        }
      }
      if (Object.keys(entries).length === 0) {
        delete byId[recordId];
        mutated = true;
      }
    }
    if (Object.keys(byId).length === 0) {
      delete queue[type];
      mutated = true;
    }
  }
  if (mutated) writeQueue(queue);
}

try { purgeStaleEntries(); } catch (e) { /* best-effort */ }

export function markDirty(recordType, recordId, fieldName, value) {
  if (!recordId || !fieldName) return;

  const queue = readQueue();
  const types = (queue[recordType] = queue[recordType] || {});
  const entries = (types[recordId] = types[recordId] || {});
  entries[fieldName] = { value, ts: Date.now() };
  writeQueue(queue);

  scheduleFlush(recordType, recordId);
}

function scheduleFlush(recordType, recordId) {
  const key = recordKey(recordType, recordId);
  let fn = debouncedFlushers.get(key);
  if (!fn) {
    fn = debounce(() => {
      flush(recordType, recordId).catch(() => {
        // Errors are logged inside flush(); the queue retains pending entries.
      });
    }, DEBOUNCE_MS);
    debouncedFlushers.set(key, fn);
  }
  fn();
}

// Snapshot the entries we're about to send so a concurrent markDirty during
// the in-flight PATCH doesn't get cleared on success.
function snapshotEntries(entries) {
  const snap = {};
  for (const [field, entry] of Object.entries(entries || {})) {
    snap[field] = { value: entry.value, ts: entry.ts };
  }
  return snap;
}

function buildPayload(snapshot) {
  const patches = {};
  const clientFieldTimestamps = {};
  for (const [field, entry] of Object.entries(snapshot)) {
    patches[field] = entry.value;
    clientFieldTimestamps[field] = entry.ts;
  }
  return {
    patches,
    clientFieldTimestamps,
    deviceId: getOrCreateDeviceId(),
  };
}

function clearFlushedEntries(recordType, recordId, snapshot) {
  const queue = readQueue();
  const entries = getRecordEntries(queue, recordType, recordId);
  if (!entries) return;
  for (const [field, snapEntry] of Object.entries(snapshot)) {
    const current = entries[field];
    // Only remove entries that haven't been re-dirtied during the flush.
    // If the user typed again mid-flight, the entry now has a newer ts and
    // must survive for the next debounce cycle.
    if (current && current.ts === snapEntry.ts) {
      delete entries[field];
    }
  }
  if (Object.keys(entries).length === 0) {
    delete queue[recordType][recordId];
    if (Object.keys(queue[recordType]).length === 0) delete queue[recordType];
  }
  writeQueue(queue);
}

export function flush(recordType, recordId) {
  const key = recordKey(recordType, recordId);
  const previous = inflightByRecord.get(key) || Promise.resolve();
  const next = previous.then(() => doFlush(recordType, recordId));
  inflightByRecord.set(key, next);
  return next;
}

async function doFlush(recordType, recordId) {
  const sender = senders.get(recordType);
  if (!sender) return null;

  const queue = readQueue();
  const entries = getRecordEntries(queue, recordType, recordId);
  if (!entries || Object.keys(entries).length === 0) return null;

  const snapshot = snapshotEntries(entries);
  const payload = buildPayload(snapshot);

  emitStatus({ status: "saving", recordType, recordId });

  try {
    const response = await sender(recordId, payload);

    // Treat the server response as authoritative. The server returns
    // {acceptedFields, rejectedFields, fieldMeta}; a field is rejected
    // when its clientFieldTimestamp is older than the server's
    // fieldMeta.lastModified (clock drift, a fresher write from another
    // tab, or a server clock running ahead). Only clear accepted fields,
    // and bump rejected entries' ts above the server's lastModified so
    // the next flush wins. Previously this cleared the entire snapshot
    // on any 200 response, which silently dropped rejected edits.
    const accepted = Array.isArray(response?.acceptedFields)
      ? response.acceptedFields
      : Object.keys(snapshot);
    const acceptedSnapshot = {};
    for (const field of accepted) {
      if (snapshot[field]) acceptedSnapshot[field] = snapshot[field];
    }
    clearFlushedEntries(recordType, recordId, acceptedSnapshot);

    const rawRejected = Array.isArray(response?.rejectedFields)
      ? response.rejectedFields
      : [];
    const reasons = response?.rejectionReasons || {};

    // Normalize: server may send strings or {field, reason} objects.
    const rejected = rawRejected.map((r) =>
      typeof r === "object" && r !== null && r.field ? r.field : r,
    );

    // Split into retryable ("stale") vs permanent ("not_allowed", "forbidden_key").
    const retryable = [];
    const permanent = [];
    for (const field of rejected) {
      const reason = reasons[field] || (typeof field === "object" ? field.reason : undefined);
      if (reason === "not_allowed" || reason === "forbidden_key") {
        permanent.push(field);
      } else {
        retryable.push(field);
      }
    }

    // Discard permanently-rejected fields so they don't loop forever.
    if (permanent.length > 0) {
      discardFields(recordType, recordId, permanent);
      // eslint-disable-next-line no-console
      console.warn("[syncQueue] permanently rejected fields", recordType, recordId, permanent);
    }

    if (retryable.length > 0) {
      bumpRejectedTimestamps(
        recordType,
        recordId,
        retryable,
        response?.fieldMeta || {},
      );
      scheduleFlush(recordType, recordId);
    }

    emitStatus({
      status: "saved",
      recordType,
      recordId,
      acceptedFields: accepted,
      rejectedFields: [...retryable, ...permanent],
    });
    return response;
  } catch (err) {
    // Leave entries in place; they'll retry on next markDirty or `online`.
    // eslint-disable-next-line no-console
    console.warn("[syncQueue] flush failed", recordType, recordId, err?.message);
    emitStatus({ status: "error", recordType, recordId, error: err });
    throw err;
  }
}

// Refresh the queued ts for fields the server rejected as stale so the
// next flush carries a timestamp strictly greater than the server's
// fieldMeta.lastModified. Falls back to Date.now() when the response
// didn't include fieldMeta for that field.
function bumpRejectedTimestamps(
  recordType,
  recordId,
  fieldNames,
  serverFieldMeta,
) {
  const queue = readQueue();
  const entries = getRecordEntries(queue, recordType, recordId);
  if (!entries) return;
  let mutated = false;
  for (const field of fieldNames) {
    if (!entries[field]) continue;
    const serverLm = serverFieldMeta?.[field]?.lastModified;
    const next =
      typeof serverLm === "number"
        ? Math.max(serverLm + 1, Date.now())
        : Date.now();
    entries[field] = { ...entries[field], ts: next };
    mutated = true;
  }
  if (mutated) writeQueue(queue);
}

// Cancel any pending debounce for this record and flush immediately. Used
// when navigating away from a screen so the next view doesn't load stale.
export async function flushNow(recordType, recordId) {
  const key = recordKey(recordType, recordId);
  const fn = debouncedFlushers.get(key);
  if (fn && typeof fn.cancel === "function") fn.cancel();
  return flush(recordType, recordId);
}

// Fire-and-forget flush of every pending record using fetch keepalive.
// Called from beforeunload and visibilitychange (hidden) — must not return
// a Promise the page will await, since the page is going away.
export function flushAllOnUnload(apiBaseUrl) {
  const queue = readQueue();
  for (const [type, byId] of Object.entries(queue)) {
    for (const [recordId, entries] of Object.entries(byId)) {
      if (!entries || Object.keys(entries).length === 0) continue;
      const snapshot = snapshotEntries(entries);
      const payload = buildPayload(snapshot);
      const url = unloadUrlFor(apiBaseUrl, type, recordId);
      if (!url) continue;
      try {
        // keepalive: true lets the request outlive the page. Body capped at
        // 64KB by the server; payload here is just dirty fields, so well
        // under in practice.
        fetch(url, {
          method: "PATCH",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
          keepalive: true,
        }).catch(() => {});
      } catch (e) {
        // ignore — page is unloading
      }
    }
  }
}

function unloadUrlFor(apiBaseUrl, recordType, recordId) {
  // Map record types to their PATCH endpoints. The path conventions match
  // the existing routes — see backend/nwd-backend/routes/*.js.
  const base = apiBaseUrl || "";
  if (recordType === "day") return `${base}/api/days/today/${recordId}`;
  if (recordType === "year") return `${base}/api/years/year/${recordId}`;
  if (recordType === "week") return `${base}/api/weeks/week/${recordId}`;
  return null;
}

let unloadHookInstalled = false;

export function installUnloadHooks(apiBaseUrl) {
  if (unloadHookInstalled || typeof window === "undefined") return;
  unloadHookInstalled = true;

  // visibilitychange (hidden) is the reliable fire-on-tab-hide signal on
  // mobile; beforeunload fires on actual close/refresh on desktop.
  window.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      flushAllOnUnload(apiBaseUrl);
    }
  });
  window.addEventListener("beforeunload", () => {
    flushAllOnUnload(apiBaseUrl);
  });
  window.addEventListener("online", () => {
    // Retry every record with pending entries.
    const queue = readQueue();
    for (const [type, byId] of Object.entries(queue)) {
      for (const recordId of Object.keys(byId)) {
        flush(type, recordId).catch(() => {});
      }
    }
  });
}

// Returns the currently-dirty fields for a record, keyed by field name.
// Used by the realtime layer to know which incoming remote patches would
// overwrite the user's in-flight typing and need a confirmation banner
// instead of silent application.
export function getPending(recordType, recordId) {
  const queue = readQueue();
  return getRecordEntries(queue, recordType, recordId) || {};
}

// Drop one or more fields from the dirty queue without sending them. Used
// when the user accepts a remote update — their local typing for those
// fields is being discarded, so the queued PATCH must be too.
export function discardFields(recordType, recordId, fieldNames) {
  if (!Array.isArray(fieldNames) || fieldNames.length === 0) return;
  const queue = readQueue();
  const entries = getRecordEntries(queue, recordType, recordId);
  if (!entries) return;
  for (const field of fieldNames) delete entries[field];
  if (Object.keys(entries).length === 0) {
    delete queue[recordType][recordId];
    if (Object.keys(queue[recordType]).length === 0) delete queue[recordType];
  }
  writeQueue(queue);
}
